import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { StageBadge } from '@/components/shared/StageBadge'
import { StatusBadge } from '@/components/shared/StatusBadge'
import { FrequencyBadge } from '@/components/shared/FrequencyBadge'
import { formatCurrency } from '@/lib/utils'
import { useBasePath } from '@/lib/paths'
import { ComplianceFilingDetailed, ComplianceFilingRecordSummary } from '@/types'
import { Plus, Calendar, AlertCircle, FileText } from 'lucide-react'
import { Link } from '@inertiajs/react'


function RecordCard({ record, basePath, filingId }: { record: ComplianceFilingRecordSummary; basePath: string; filingId: string }) {
  return (
    <Link
      href={`${basePath}/compliance_filings/${filingId}/records/${record.id}`}
      className="block border rounded-lg p-4 hover:bg-muted/50 transition-colors"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium">
            {new Date(record.period_start_date).toLocaleDateString()} - {new Date(record.period_end_date).toLocaleDateString()}
          </span>
        </div>
        <StageBadge stage={record.stage} variant="compliance" />
      </div>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span>Due: {new Date(record.due_date).toLocaleDateString()}</span>
        {record.overdue && (
          <span className="text-red-600 font-medium flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Overdue
          </span>
        )}
      </div>
    </Link>
  )
}

type Props = {
  filing: ComplianceFilingDetailed
  type: { id: string; name: string; gov_fee_cents: number; service_fee_cents: number }
}

export default function ComplianceFilingsShow({ filing, type }: Props) {
  const basePath = useBasePath()

  return (
    <DashboardLayout title={filing.type_name} breadcrumbs={[{ label: 'Compliance Filings', href: 'compliance_filings' }, { label: filing.type_name }]}>
      <div>
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-xl font-semibold tracking-tight">{filing.type_name}</h1>
            <StatusBadge status={filing.status} />
            <FrequencyBadge frequency={filing.frequency} />
          </div>
          <p className="text-sm text-muted-foreground">{filing.business_name} · {filing.jurisdiction_label || filing.type_code}</p>
          <div className="flex items-center gap-2 mt-3">
            <Button asChild size="sm" variant="outline">
              <Link href={`${basePath}/compliance_filings/${filing.id}/documents`}>
                <FileText className="w-3.5 h-3.5 mr-1.5" />
                Documents
              </Link>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="border rounded-lg px-4 py-3">
            <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Start Date</p>
            <p className="text-sm">{new Date(filing.start_date).toLocaleDateString()}</p>
          </div>
          <div className="border rounded-lg px-4 py-3">
            <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Next Due Date</p>
            <p className={`text-sm ${filing.next_due_date && new Date(filing.next_due_date) < new Date() ? 'text-red-600 font-medium' : ''}`}>
              {filing.next_due_date ? new Date(filing.next_due_date).toLocaleDateString() : '—'}
            </p>
          </div>
          <div className="border rounded-lg px-4 py-3">
            <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Cost per Period</p>
            <p className="text-sm">{formatCurrency(type.gov_fee_cents + type.service_fee_cents)}</p>
          </div>
        </div>

        <div className="space-y-6">
          <section className="border rounded-lg">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground">Filing Records</h2>
              {filing.status === 'active' && (
                <Button asChild size="sm" variant="outline">
                  <Link href={`${basePath}/compliance_filings/${filing.id}/records/new`}>
                    <Plus className="w-3.5 h-3.5 mr-1.5" />
                    File Next Period
                  </Link>
                </Button>
              )}
            </div>
            <div className="p-4">
              {filing.records.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No filing records yet</p>
              ) : (
                <div className="space-y-3">
                  {filing.records.map(record => (
                    <RecordCard key={record.id} record={record} basePath={basePath} filingId={filing.id} />
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </DashboardLayout>
  )
}
